import {
  PortalArticleBlock,
  PortalArticleNoteStyle,
  PortalArticleSection,
  PortalArticleSectionContentItem,
  PortalArtigoModel,
  PortalFaqAnswerBlock,
  PortalFaqConfig,
} from '../../../models/PortalArtigoModel'
import {
  normalizePortalRichText,
  normalizePortalRichTextList,
} from './rich-text-utils'
import { normalizePortalTable } from './portal-table'
import { extractYoutubeVideoId } from './youtube-utils'

export function normalizePortalArtigoForSave(artigo: PortalArtigoModel): PortalArtigoModel {
  const isPillar = artigo.tipo === 'PILLAR'

  return {
    ...artigo,
    tipo: isPillar ? 'PILLAR' : null,
    imagemThumbUrl: normalizeOptionalText(artigo.imagemThumbUrl),
    imagemThumbAlt: normalizeOptionalText(artigo.imagemThumbAlt),
    tituloOg: normalizeOptionalText(artigo.tituloOg),
    descricaoOg: normalizeOptionalText(artigo.descricaoOg),
    imagemOgUrl: normalizeOptionalText(artigo.imagemOgUrl),
    imagemOgAlt: normalizeOptionalText(artigo.imagemOgAlt),
    configuracaoPillar: isPillar ? normalizePillarConfig(artigo.configuracaoPillar) : undefined,
    conteudo: (artigo.conteudo || []).map(normalizePortalArticleBlock),
    faq: artigo.faq ? normalizePortalFaq(artigo.faq) : artigo.faq,
    artigosRelacionados: artigo.artigosRelacionados
      ? {
          ...artigo.artigosRelacionados,
          anchorId: artigo.artigosRelacionados.anchorId.trim(),
          title: artigo.artigosRelacionados.title.trim(),
          articleSlugs: artigo.artigosRelacionados.articleSlugs.map((slug) => slug.trim()),
        }
      : artigo.artigosRelacionados,
    cta: isPillar
      ? null
      : artigo.cta
        ? {
            ...artigo.cta,
            paragraphs: normalizePortalRichTextList(artigo.cta.paragraphs),
          }
        : artigo.cta,
  }
}

function normalizeOptionalText(value?: string) {
  return value?.trim() || undefined
}

function normalizePillarConfig(config: PortalArtigoModel['configuracaoPillar']) {
  if (!config) return undefined

  return {
    ...config,
    sidebar: config.sidebar
      ? {
          ...config.sidebar,
          indexTitle: config.sidebar.indexTitle?.trim(),
          indexLabels: config.sidebar.indexLabels
            ? Object.fromEntries(
                Object.entries(config.sidebar.indexLabels).map(([key, label]) => [
                  key,
                  label.trim(),
                ])
              )
            : undefined,
          indexCta: config.sidebar.indexCta
            ? {
                label: config.sidebar.indexCta.label?.trim(),
                href: config.sidebar.indexCta.href?.trim(),
              }
            : undefined,
          banner: config.sidebar.banner
            ? {
                ...config.sidebar.banner,
                title: config.sidebar.banner.title?.trim(),
                paragraphs: normalizePortalRichTextList(config.sidebar.banner.paragraphs),
                label: config.sidebar.banner.label?.trim(),
                href: config.sidebar.banner.href?.trim(),
                image: config.sidebar.banner.image
                  ? {
                      src: config.sidebar.banner.image.src?.trim(),
                      alt: config.sidebar.banner.image.alt?.trim(),
                      href: config.sidebar.banner.image.href?.trim(),
                    }
                  : undefined,
              }
            : undefined,
        }
      : undefined,
  }
}

function normalizePortalArticleBlock(block: PortalArticleBlock): PortalArticleBlock {
  if (block.type === 'summary') return block

  if (block.type === 'intro') {
    return {
      ...block,
      section: normalizeNewSection(block.section, true, `${block.id}-section`),
    }
  }

  if (block.type === 'topic') {
    return {
      ...block,
      items: block.items.map((item) => {
        if (item.type === 'image') {
          return {
            type: 'image',
            id: item.id,
            anchorId: item.anchorId,
            image: {
              ...item.image,
              href: normalizeOptionalText(item.image.href),
              caption: normalizePortalRichText(item.image.caption),
            },
          }
        }
        if (item.type === 'quickSummary') {
          return {
            ...item,
            quickSummary: {
              title: item.quickSummary.title?.trim() || 'Resumo rápido:',
              paragraph: normalizePortalRichText(item.quickSummary.paragraph) || '',
              items: normalizePortalRichTextList(item.quickSummary.items),
            },
          }
        }
        return {
          ...item,
          section: normalizeNewSection(item.section, false, `${item.id}-section`),
        }
      }),
    }
  }

  throw new Error(`Bloco raiz legado não pode ser salvo: ${(block as unknown as { type: string }).type}`)
}

function normalizeNewSection(
  section: PortalArticleSection,
  isIntro = false,
  fallbackId?: string
) {
  return {
    id: section.id || fallbackId || '',
    title: section.title || '',
    anchorId: section.anchorId,
    subtitle: section.subtitle,
    content: normalizeSectionContent(section.content, isIntro),
    extraAnchorIds: section.extraAnchorIds,
  }
}

function normalizeSectionContent(
  content: PortalArticleSectionContentItem[] | undefined,
  isIntro = false
) {
  if (!Array.isArray(content)) return []

  return content.reduce<PortalArticleSectionContentItem[]>((items, item) => {
    if (!item) return items

    if (item.type === 'paragraph') {
      items.push({
        ...item,
        content: normalizePortalRichText(item.content) || '',
      })
      return items
    }

    if (item.type === 'image') {
      items.push({
        type: 'image',
        id: item.id,
        anchorId: item.anchorId,
        image: {
          ...item.image,
          href: normalizeOptionalText(item.image.href),
          caption: normalizePortalRichText(item.image.caption),
        },
      })
      return items
    }

    if (item.type === 'youtube') {
      items.push({
        type: 'youtube',
        id: item.id,
        videoId: extractYoutubeVideoId(item.videoId) || item.videoId.trim(),
      })
      return items
    }

    if (item.type === 'table') {
      if (!isIntro) items.push(normalizePortalTable(item))
      return items
    }

    if (isIntro) return items

    if (item.type === 'bullets') {
      items.push({
        ...item,
        items: normalizePortalRichTextList(item.items),
      })
      return items
    }

    if (item.type === 'note') {
      items.push({
        ...item,
        content: normalizePortalRichText(item.content) || '',
        noteStyle: normalizeNoteStyle(item.noteStyle),
      })
      return items
    }

    if (item.type === 'cards') {
      items.push({
        ...item,
        title: undefined,
        items: (item.items || []).map((cardItem) => ({ ...cardItem })),
      })
    }

    return items
  }, [])
}

function normalizeNoteStyle(noteStyle?: PortalArticleNoteStyle) {
  if (!noteStyle) return undefined

  return {
    ...noteStyle,
    borderColor: noteStyle.borderColor?.trim(),
    backgroundColor: noteStyle.backgroundColor?.trim(),
    textColor: noteStyle.textColor?.trim(),
    strongColor: noteStyle.strongColor?.trim(),
    linkColor: noteStyle.linkColor?.trim(),
  }
}

function normalizePortalFaq(faq: PortalFaqConfig): PortalFaqConfig {
  return {
    ...faq,
    items: (faq.items || []).map((item) => ({
      ...item,
      answer: normalizePortalFaqAnswer(item.answer),
    })),
    whatsapp: {
      ...faq.whatsapp,
      description: normalizePortalRichText(faq.whatsapp?.description) || '',
    },
  }
}

function normalizePortalFaqAnswer(answer: PortalFaqAnswerBlock[]) {
  return (answer || []).map((answerBlock) => {
    if (answerBlock.type === 'paragraph') {
      return {
        ...answerBlock,
        content: normalizePortalRichText(answerBlock.content) || '',
      }
    }

    return {
      ...answerBlock,
      items: normalizePortalRichTextList(answerBlock.items),
    }
  })
}
